import React, { useState, useMemo, useCallback } from 'react';
import { VblForecastResult, VblForecastConfiguration, VblForecastFilters, VBLForecastForCaseUpdate } from '@/types/vblForecast';
import type { TurbineToForecast } from '@/types/vbl';
import { TableCard, Tooltip } from '@/components/ui';
import {
  ExclamationTriangleIcon,
  ClockIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  ArrowUturnLeftIcon
} from '@heroicons/react/24/outline';
import { externalLinks } from '@/config/externalLinks';
import { vblForecastApi } from '@/services/api/vblForecastApi';
import { ErrorModal } from '@/components/error/ErrorModal';
import { AxiosError } from 'axios';
import { featureManager } from '@/services/featureManager/featureManager';

interface VblForecastResultsTableProps {
  results: VblForecastResult[];
  configuration: VblForecastConfiguration;
  filters: VblForecastFilters;
  canEditRecords?: boolean;
  onEditRow?: (turbine: TurbineToForecast) => void;
  closedOffFailureIds?: Set<string>;
  onUndoClosure?: (failureId: string) => void;
}

interface VBLCaseStatus { [key: string]: 'not_updated' | 'in_progress' | 'success' | 'failed'; }

export const VblForecastResultsTable: React.FC<VblForecastResultsTableProps> = ({
  results,
  configuration,
  filters,
  canEditRecords = false,
  onEditRow,
  closedOffFailureIds,
  onUndoClosure,
}) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'timeToFailure', direction: 'asc' });

  const [vblUpdateStatus, setVBLUpdateStatus] = useState<VBLCaseStatus>({});
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [apiError, setApiError] = useState(null);

  const getButtonStatus = useCallback((status: 'not_updated' | 'in_progress' | 'success' | 'failed') => {
    switch (status) {
      case 'not_updated':
        return <button className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-blue-600 text-white" title='Update Case with VBL values'>Update</button>
      case 'in_progress':
        return <button disabled={true} className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-blue-200" title='Updating Case with VBL values'><div className='animate-spin rounded-full h-2 w-2 border-b-2 mx-5 my-1 border-blue-600'></div></button>
      case 'success':
        return <button disabled={true} className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-green-600 text-white" title='Updated Case with VBL values'>Success</button>
      case 'failed':
        return <button disabled={true} className="px-3 py-2 rounded-md text-sm font-medium transition-colors bg-red-600 text-white" title='Failed to update Case with VBL values'>Failed</button>
    }
  }, []);

  const handleAPIError = async (error: AxiosError) => {
    const parseError: string = typeof error.response?.data === "string" ? JSON.parse(error.response.data) : error.response.data;
    let errorMessage = parseError['msg'] ? parseError['msg'] : parseError['error'];
    if (!errorMessage) {
      errorMessage = "An error occurred with the last request";
    }
    setApiError(errorMessage);
    setShowErrorModal(true);
  }

  async function updateCaseWithVBL(row: VblForecastResult) {
    const caseId = row.turbine.case_id;
    // Functional updaters: take prev, return a new object. Mutating the existing
    // state object (as the old code did) bypasses React's change detection and
    // triggered a fistful of "update inside test was not wrapped in act(...)"
    // warnings; the manual forceUpdate() was a workaround that masked it.
    setVBLUpdateStatus(prev => ({ ...prev, [caseId]: "in_progress" as const }));
    const vbl: VBLForecastForCaseUpdate = {
      "VBL Estimated Failure Date": Math.floor(new Date(row.predictedFailureDate).getTime() / 1000),
      "VBL Lower CI Date": Math.floor(new Date(row.confidenceInterval.lower).getTime() / 1000),
      "VBL Upper CI Date": Math.floor(new Date(row.confidenceInterval.upper).getTime() / 1000)
    }

    let nextStatus: 'not_updated' | 'in_progress' | 'success' | 'failed';
    try {
      const res = await vblForecastApi.updateCaseWithVBL(caseId, vbl);
      nextStatus = res.msg === "OK" ? "success" : "failed";
    } catch (err) {
      nextStatus = "failed";
      handleAPIError(err);
    }
    setVBLUpdateStatus(prev => ({ ...prev, [caseId]: nextStatus }));
  };

  // Combined filtering and sorting (following Weibull Analysis pattern)
  const filteredAndSortedResults = useMemo(() => {
    // Results are already deduplicated at page level, just apply user filters
    let filtered = results;

    // Apply user filters
    if (filters.sites.length > 0) {
      filtered = filtered.filter(result => filters.sites.includes(result.turbine.site_name));
    }
    if (filters.turbineModels.length > 0) {
      filtered = filtered.filter(result => filters.turbineModels.includes(result.turbine.model_name));
    }
    if (filters.componentsMajor.length > 0) {
      filtered = filtered.filter(result => filters.componentsMajor.includes(result.turbine.major_component_name || ''));
    }
    if (filters.componentsMinor.length > 0) {
      filtered = filtered.filter(result => filters.componentsMinor.includes(result.turbine.component_name || ''));
    }

    // Apply sorting
    if (sortConfig) {
      filtered.sort((a, b) => {
        let aValue: string | number | Date;
        let bValue: string | number | Date;

        switch (sortConfig.key) {
          case 'case_id':
            // Natural/alphanumeric sorting
            return a.turbine.case_id.toString().localeCompare(b.turbine.case_id.toString(), undefined, {
              numeric: true,
              sensitivity: 'base'
            }) * (sortConfig.direction === 'asc' ? 1 : -1);
          case 'site_name':
            aValue = a.turbine.site_name;
            bValue = b.turbine.site_name;
            break;
          case 'turbine_name':
            aValue = a.turbine.turbine_name;
            bValue = b.turbine.turbine_name;
            break;
          case 'component':
            aValue = a.turbine.major_component_name || '';
            bValue = b.turbine.major_component_name || '';
            break;
          case 'subcomponent':
            aValue = a.turbine.component_name || '';
            bValue = b.turbine.component_name || '';
            break;
          case 'failure_mode':
            aValue = a.turbine.failure_mode_name || '';
            bValue = b.turbine.failure_mode_name || '';
            break;
          case 'predictedFailureDate':
            aValue = new Date(a.predictedFailureDate);
            bValue = new Date(b.predictedFailureDate);
            break;
          case 'timeToFailure':
            aValue = a.timeToFailure.days;
            bValue = b.timeToFailure.days;
            break;
          case 'lowerCI':
            aValue = new Date(a.confidenceInterval.lower);
            bValue = new Date(b.confidenceInterval.lower);
            break;
          case 'upperCI':
            aValue = new Date(a.confidenceInterval.upper);
            bValue = new Date(b.confidenceInterval.upper);
            break;
          case 'riskLevel': {
            const riskOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
            aValue = riskOrder[a.riskLevel as keyof typeof riskOrder] || 0;
            bValue = riskOrder[b.riskLevel as keyof typeof riskOrder] || 0;
            break;
          }
          case 'inspectionSeverity':
            aValue = a.lastInspectionSeverity;
            bValue = b.lastInspectionSeverity;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return filtered;
  }, [results, filters, sortConfig]);

  // Handle sorting (3-state toggle pattern from Weibull Analysis)
  const handleSort = (key: string) => {
    setSortConfig((prevSort) => {
      if (prevSort?.key === key) {
        // 3-state toggle: asc -> desc -> null -> asc
        if (prevSort.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (prevSort.direction === 'desc') {
          return null; // No sorting
        }
      }
      // New column or returning from null, default to ascending
      return { key, direction: 'asc' };
    });
  };

  // Get sort icon
  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Risk level styling
  const getRiskLevelStyle = (riskLevel: VblForecastResult['riskLevel']) => {
    switch (riskLevel) {
      case 'High':
        return 'bg-red-100 text-red-800';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'Low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Risk level icon
  const getRiskLevelIcon = (riskLevel: VblForecastResult['riskLevel']) => {
    switch (riskLevel) {
      case 'High':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      case 'Medium':
        return <ClockIcon className="h-4 w-4" />;
      case 'Low':
        return <CheckCircleIcon className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getSeverityColor = (severityName: string) => {
    switch (severityName.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'good':
      case 'progressed':
        return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };


  // Statistics
  const stats = useMemo(() => {
    const total = filteredAndSortedResults.length;  // Use sorted/filtered results for stats
    const totalMapped = results.filter(result => result.hasMapping && result.predictedFailureDate).length;
    const high = filteredAndSortedResults.filter(r => r.riskLevel === 'High').length;
    const medium = filteredAndSortedResults.filter(r => r.riskLevel === 'Medium').length;
    const low = filteredAndSortedResults.filter(r => r.riskLevel === 'Low').length;
    const unmapped = results.filter(r => !r.hasMapping).length;

    return { total, totalMapped, high, medium, low, unmapped };
  }, [filteredAndSortedResults, results]);

  return (
    <div className="space-y-4">
      {/* Results Table */}
      <TableCard
        title="VBL Forecast Results"
        subtitle={`Showing ${stats.total} of ${stats.totalMapped} turbines with ${configuration.confidenceLevel * 100}% confidence intervals`}
      >
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {canEditRecords && (
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" />
                )}
                <th
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('case_id')}
                >
                  Case ID{getSortIcon('case_id')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('site_name')}
                >
                  Site{getSortIcon('site_name')}
                </th>
                <th
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('turbine_name')}
                >
                  Turbine Name{getSortIcon('turbine_name')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('component')}
                >
                  Component{getSortIcon('component')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('subcomponent')}
                >
                  Sub Component{getSortIcon('subcomponent')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('failure_mode')}
                >
                  Failure Mode{getSortIcon('failure_mode')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('predictedFailureDate')}
                >
                  Estimated Shutdown Date{getSortIcon('predictedFailureDate')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('lowerCI')}
                >
                  Lower CI{getSortIcon('lowerCI')}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('upperCI')}
                >
                  Upper CI{getSortIcon('upperCI')}
                </th>
                <th
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('riskLevel')}
                >
                  Risk{getSortIcon('riskLevel')}
                </th>
                <th
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('inspectionSeverity')}
                >
                  Last Inspection Severity{getSortIcon('inspectionSeverity')}
                </th>
                {featureManager.useBpFeature() &&
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100">
                    Update Case in AI HUB
                  </th>
                }
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAndSortedResults.map((result) => {
                const isClosedOff = closedOffFailureIds?.has(result.turbine.failure_id) ?? false;
                const rowClassName = isClosedOff
                  ? 'bg-gray-50 text-gray-400 italic hover:bg-gray-100'
                  : 'hover:bg-gray-50';
                return (
                  <tr key={result.turbine.failure_id} className={rowClassName}>
                    {/* Edit / Close Off / Undo button */}
                    {canEditRecords && (
                      <td className="px-2 py-4 whitespace-nowrap">
                        {isClosedOff ? (
                          <Tooltip content="Undo close-off">
                            <button
                              type="button"
                              onClick={() => onUndoClosure?.(result.turbine.failure_id)}
                              className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
                            >
                              <ArrowUturnLeftIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                        ) : (
                          <Tooltip content="Edit VBL Record">
                            <button
                              type="button"
                              onClick={() => onEditRow?.(result.turbine)}
                              className="p-1 text-gray-400 hover:text-onyx-bright-blue transition-colors"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </Tooltip>
                        )}
                      </td>
                    )}

                    {/* Case ID */}
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium">
                      {result.turbine.case_id ? (featureManager.useBpFeature() ? (
                        <a
                          href={externalLinks.aiHub.caseManagement(encodeURIComponent(String(result.turbine.case_id)))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                        >
                          {result.turbine.case_id}
                        </a>
                      ) : (
                        <div className="text-gray-900 truncate max-w-[60px]" title={String(result.turbine.case_id)}>
                          {result.turbine.case_id}
                        </div>
                      )) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>

                    {/* Site */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="truncate max-w-[100px]" title={result.turbine.site_name}>
                        {result.turbine.site_name}
                      </div>
                    </td>

                    {/* Turbine Name */}
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="truncate max-w-[120px]" title={result.turbine.turbine_name}>
                        {result.turbine.turbine_name}
                      </div>
                    </td>

                    {/* Component */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="truncate max-w-[120px]" title={result.turbine.major_component_name || 'N/A'}>
                        {result.turbine.major_component_name || 'N/A'}
                      </div>
                    </td>

                    {/* Sub Component */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="truncate max-w-[120px]" title={result.turbine.component_name || 'N/A'}>
                        {result.turbine.component_name || 'N/A'}
                      </div>
                    </td>

                    {/* Failure Mode */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="truncate max-w-[120px]" title={result.turbine.failure_mode_name || 'N/A'}>
                        {result.turbine.failure_mode_name || 'N/A'}
                      </div>
                    </td>

                    {/* Predicted Shutdown Date */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <div>
                          <div>{new Date(result.predictedFailureDate).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">
                            {Math.round(result.timeToFailure.days)} days
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Lower CI */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <div>
                          <div>{new Date(result.confidenceInterval.lower).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">
                            {Math.round(result.timeToFailure.confidenceInterval.lower)} days
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Upper CI */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <div>
                          <div>{new Date(result.confidenceInterval.upper).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500">
                            {Math.round(result.timeToFailure.confidenceInterval.upper)} days
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Risk Level */}
                    <td className="px-2 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskLevelStyle(result.riskLevel)}`}>
                        {getRiskLevelIcon(result.riskLevel)}
                        <span className="ml-1">{result.riskLevel}</span>
                      </span>
                    </td>

                    {/* Last Inspection Severity */}
                    <td className="px-2 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(result.turbine.inspection_severity ? result.turbine.inspection_severity : '')}`}>
                        {result.turbine.inspection_severity ? result.turbine.inspection_severity : ''}
                      </span>
                    </td>

                    {/* Update case button */}
                    {featureManager.useBpFeature() &&
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div onClick={() => updateCaseWithVBL(result)}>{getButtonStatus(vblUpdateStatus[result.turbine.case_id] ?? "not_updated")}</div>
                      </td>
                    }
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Empty State */}
          {filteredAndSortedResults.length === 0 && (
            <div className="text-center py-8">
              <XCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No forecast results</h3>
              <p className="mt-1 text-sm text-gray-500">
                No turbines have workspace mappings or valid VBL parameters for forecasting.
              </p>
            </div>
          )}
        </div>
      </TableCard>

      {/* Unmapped Turbines Warning */}
      {stats.unmapped > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                {stats.unmapped} turbines without workspace mappings
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  These turbines cannot be forecasted because they don&apos;t have associated VBL analysis workspaces.
                  Configure workspace mappings in the VBL Config page to enable forecasting for these turbines.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {showErrorModal && (
        <ErrorModal open={showErrorModal} onClose={() => setShowErrorModal(false)} error={apiError} />
      )}
    </div>

  );
};